import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { BookingLanguage, TimeSlot } from "@/types/booking";
import { Droplet, Zap, Clock } from "lucide-react";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { t as translate } from "@/locales";
import { languageMap } from "@/i18n";

// Women's hormone add-on product ID
const WOMEN_HORMONE_PRODUCT_ID = "e57e0619-d6bf-4f27-9e42-cecc4ebcaabb";

interface TimeSlotsDisplayProps {
  availableSlots: string[];
  selectedTimes: string[];
  onTimeSelect: (time: string) => void;
  language: BookingLanguage;
  bookingType?: "request";
  fastBookingSlots?: string[];
  allSelectedTimeSlots?: TimeSlot[];
  date?: Date;
  hideTitle?: boolean; // Option to hide the title when used within SlotPreferenceBlock
  preferenceOverride?: "first" | "second";
  allowSelectionOverride?: boolean;
  // External slot availability indicators
  externalSlots?: string[]; // List of times from external system (instant confirmation)
  showSlotIndicators?: boolean; // Whether to show instant/request indicators
}

const TimeSlotsDisplay = ({
  availableSlots,
  selectedTimes,
  onTimeSelect,
  language,
  bookingType = "request",
  fastBookingSlots = [],
  allSelectedTimeSlots = [],
  date,
  hideTitle = false,
  preferenceOverride,
  allowSelectionOverride = false,
  externalSlots = [],
  showSlotIndicators = false,
}: TimeSlotsDisplayProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });

  // Get add-on and product data from store for hormone hint
  const selectedAddOns =
    useBookingFlowStore((state) => state.selectedAddOns) || [];
  const selectedProducts =
    useBookingFlowStore((state) => state.selectedProducts) || [];
  const allProducts = useBookingFlowStore((state) => state._products) || [];

  // Get selected product details by filtering all products
  const selectedProductDetails = allProducts.filter((p) =>
    selectedProducts.includes(p.id),
  );

  // Check if any selected add-on has a warning (hides "Best" badge)
  const selectedAddOnDetails = allProducts.filter((p) =>
    selectedAddOns.includes(p.id),
  );
  const hasAddOnWarnings = selectedAddOnDetails.some((addon) => addon.warning);

  // Check if women's hormone hint should be shown
  const hasWomenHormoneAddOn = selectedAddOns.includes(
    WOMEN_HORMONE_PRODUCT_ID,
  );
  // Check if any selected package includes women's hormone as a component
  const hasPackageWithWomenHormone = selectedProductDetails.some(
    (product) =>
      product.product_type === "package" &&
      product.components?.includes(WOMEN_HORMONE_PRODUCT_ID),
  );
  const showHormoneHint = hasWomenHormoneAddOn || hasPackageWithWomenHormone;
  // Show "for women" disclaimer when it's included in a package (not direct add-on selection)
  const needsForWomenDisclaimer =
    hasPackageWithWomenHormone && !hasWomenHormoneAddOn;

  const totalSelectedCount = allSelectedTimeSlots.length;
  const effectivePreference = preferenceOverride
    ? preferenceOverride
    : totalSelectedCount === 0
      ? "first"
      : "second";

  const hasReachedMaxRegularSlots =
    !allowSelectionOverride &&
    effectivePreference === "second" &&
    totalSelectedCount >= 2;

  // Check if current date is the same as the first selected slot's date
  // If so, disable all slots for second preference
  const isCurrentDateSameAsFirstSlot = () => {
    if (
      totalSelectedCount === 0 ||
      !date ||
      allSelectedTimeSlots.length === 0
    ) {
      return false;
    }

    const firstSlotDate = allSelectedTimeSlots[0].date;
    return (
      date.getDate() === firstSlotDate.getDate() &&
      date.getMonth() === firstSlotDate.getMonth() &&
      date.getFullYear() === firstSlotDate.getFullYear()
    );
  };

  const shouldDisableSecondPreference =
    effectivePreference === "second" &&
    !allowSelectionOverride &&
    totalSelectedCount >= 1 &&
    isCurrentDateSameAsFirstSlot();

  // Format the time to display in a more human-readable way (optional)
  const formatDisplayTime = (time: string) => {
    return time; // We're keeping the original format (HH:MM) for now
  };

  // Create title text based on how many slots are already selected
  const getRequestTitle = () => {
    return effectivePreference === "first"
      ? t("slotPreference.firstSlotPreference")
      : t("slotPreference.secondSlotPreference");
  };

  // Create optional text for second slot
  const getOptionalText = () => {
    if (effectivePreference === "first") {
      return null;
    }
    return t("slotPreference.optional");
  };

  // Get "Best" label text
  const getBestLabel = () => {
    return t("slotPreference.best");
  };

  // Helper function to check if time is <= 09:30
  const isEarlyMorningSlot = (time: string) => {
    const [hours, minutes] = time.split(":").map(Number);
    return hours < 9 || (hours === 9 && minutes <= 30);
  };

  // Rank each slot by time of day, independent of the order `availableSlots`
  // arrives in: `getBookableTimesForDate` sorts only on its "external +
  // opening-hours" branch, and returns the partner's published order on the
  // two external-only branches. Render order still follows `availableSlots`.
  const slotChronologicalRank = new Map(
    [...availableSlots].sort().map((time, i) => [time, i]),
  );

  console.log("TimeSlotsDisplay rendering with:", {
    availableSlots,
    selectedTimes,
    language,
    totalSelectedCount,
    effectivePreference,
    hasReachedMaxRegularSlots,
    shouldDisableSecondPreference,
    // Hormone hint debug
    selectedAddOns,
    selectedProducts,
    selectedProductDetails: selectedProductDetails.map((p) => ({
      id: p.id,
      name: p.name,
      product_type: p.product_type,
      components: p.components,
    })),
    showHormoneHint,
    hasWomenHormoneAddOn,
    hasPackageWithWomenHormone,
    WOMEN_HORMONE_PRODUCT_ID,
  });

  return (
    <div className="space-y-3">
      {/* Show title when we have slots */}
      {availableSlots.length > 0 && !hideTitle && (
        <h3 className="text-sm font-semibold text-gray-900">
          {getRequestTitle()}
          {getOptionalText() && (
            <span className="text-xs font-normal text-gray-500 ml-2">
              ({getOptionalText()})
            </span>
          )}
        </h3>
      )}

      {/* All Time Slots - now includes both regular and "Best" (fast booking) slots */}
      {availableSlots.length > 0 ? (
        <div className="space-y-3">
          <div className="grid grid-cols-3 lg:grid-cols-4 gap-2">
            {availableSlots.map((time) => {
              const isTimeSelected = selectedTimes.includes(time);
              const isBestSlot =
                fastBookingSlots.includes(time) || isEarlyMorningSlot(time);
              const isSlotDisabled =
                !isTimeSelected &&
                (hasReachedMaxRegularSlots || shouldDisableSecondPreference);
              // Check if this slot is from external system (instant confirmation)
              const isInstantSlot =
                showSlotIndicators && externalSlots.includes(time);
              const isRequestSlot =
                showSlotIndicators && !externalSlots.includes(time);

              return (
                <button
                  key={`slot-${time}`}
                  // Ranked, not timed: the slot TIME is whatever the clinic has
                  // free on the day a browser test was recorded, so a test
                  // identifying this button by its text breaks as soon as
                  // availability moves (EAGNT-744). The rank is what the step
                  // means — "the earliest offered slot". Scoped by preference
                  // because both preference blocks are mounted at once, and the
                  // replay resolves a test id only when one element carries it.
                  data-testid={`time-slot-${preferenceOverride ?? "only"}-${
                    (slotChronologicalRank.get(time) ?? 0) + 1
                  }`}
                  onClick={() => !isSlotDisabled && onTimeSelect(time)}
                  disabled={isSlotDisabled}
                  className={cn(
                    "min-h-[40px] px-2 py-1 font-normal rounded-[10px] flex items-center justify-center gap-1.5 transition-all duration-200 border text-[15px]",
                    isTimeSelected
                      ? "bg-light-blue-300 text-neutral-900 hover:bg-light-blue-300 hover:text-neutral-900 focus:bg-light-blue-300 focus:text-neutral-900 font-medium border-light-blue-300 cursor-pointer"
                      : isSlotDisabled
                        ? "text-gray-300 opacity-40 !cursor-not-allowed hover:bg-transparent border border-gray-100"
                        : isInstantSlot
                          ? "bg-deep-green-50 border-deep-green-200 text-neutral-900 hover:bg-deep-green-100 cursor-pointer"
                          : isBestSlot
                            ? "border-gray-200 text-neutral-900 hover:bg-gray-50 cursor-pointer"
                            : "border-gray-200 text-neutral-900 hover:bg-gray-50 cursor-pointer",
                  )}
                >
                  {/* Slot indicator icons */}
                  {showSlotIndicators && !isTimeSelected && isInstantSlot && (
                    <Zap className="h-3.5 w-3.5 text-deep-green-500 fill-deep-green-500" />
                  )}
                  {showSlotIndicators && !isTimeSelected && isRequestSlot && (
                    <Clock className="h-3.5 w-3.5 text-gray-400" />
                  )}
                  <span className="text-[15px]">{formatDisplayTime(time)}</span>
                  {isBestSlot &&
                    !isTimeSelected &&
                    !showSlotIndicators &&
                    !hasAddOnWarnings && (
                      <span className="text-[9px] font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded uppercase leading-none">
                        {getBestLabel()}
                      </span>
                    )}
                </button>
              );
            })}
          </div>

          {/* Helper text explaining best slots */}
          {availableSlots.some(
            (time) =>
              fastBookingSlots.includes(time) || isEarlyMorningSlot(time),
          ) && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              {/* Lead with the same blue "Best" badge shown on the slots, so the
                  explanation visibly correlates with the marked times. */}
              <span className="shrink-0 self-start mt-0.5 text-[9px] font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded uppercase leading-none">
                {getBestLabel()}
              </span>
              <p className="text-sm text-gray-700">
                {translate(language, "booking.bestSlotTooltip")}
              </p>
            </div>
          )}

          {/* Legend explaining slot availability indicators */}
          {showSlotIndicators &&
            (() => {
              return (
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-deep-green-500 fill-deep-green-500 flex-shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {translate(
                          language,
                          "slotPreference.instantConfirmation",
                        )}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        —{" "}
                        {translate(
                          language,
                          "slotPreference.instantConfirmationDesc",
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-gray-900">
                        {translate(language, "slotPreference.requestNeeded")}
                      </span>
                      <span className="text-sm text-gray-500 ml-1">
                        —{" "}
                        {translate(
                          language,
                          "slotPreference.requestNeededDesc",
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

          {/* Hormone hint for women's hormone blood test */}
          {showHormoneHint && (
            <div className="flex items-start gap-2 p-3 bg-aubergine-100 rounded-lg border border-aubergine-200">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-aubergine-200 flex items-center justify-center mt-0.5">
                <Droplet className="h-3 w-3 text-aubergine-800 fill-aubergine-800" />
              </div>
              <p className="text-sm text-aubergine-800">
                {needsForWomenDisclaimer && (
                  <span className="font-medium">
                    {translate(language, "hormoneHint.forWomen")}
                  </span>
                )}
                {translate(language, "hormoneHint.text")}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
          <p>{t("slotPreference.noSlotsAvailable")}</p>
        </div>
      )}
    </div>
  );
};

export default TimeSlotsDisplay;
